import debounce from '@/utils/debounce.js';

export default {
  data() {
    return {
      shouldShowBudgetError: false,
      debouncedCheckBudget: null,
    };
  },
  created() {
    this.debouncedCheckBudget = debounce(() => {
      const editor = this.$refs.budgetEditor;
      this.shouldShowBudgetError = this.budget && editor && editor.minBudget !== null && this.budget < editor.minBudget;
    }, 800);
  },
  computed: {
    budgetPeriods() {
      return this.$store.getters['app/budgetPeriods'];
    },
    budgetLevel() {
      return this.isCampaignLevel ? 'campaign' : 'adset';
    },
    budgetPeriod() {
      switch (this.budgetLevel) {
        case 'campaign':
          return this.entity.output_campaign.budget_period;

        case 'adset':
          return this.entity.settings.adset_budget_period;
      }

      return null;
    },
    budget() {
      switch (this.budgetLevel) {
        case 'campaign':
          return this.entity.output_campaign.budget;

        case 'adset':
          return this.entity.settings.adset_budget;
      }

      return null;
    },
    isLifetimeRangeEditable() {
      return !!this.entity.isNew && !this.disabled;
    },
    invalidLifetimeRange() {
      if (this.budgetPeriod !== 'lifetime') {
        return false;
      }

      if (!this.entity.settings.adset_start_time || !this.entity.settings.adset_end_time) {
        return true;
      }

      return this.entity.settings.adset_end_time.getTime() - this.entity.settings.adset_start_time.getTime() < 86400000;
    },
    invalidLifetimeRangeInfoText() {
      if (this.entity.settings.adset_start_time >= this.entity.settings.adset_end_time) {
        return this.tr('End date must be greater than start date!');
      }
      if (Math.abs(this.entity.settings.adset_end_time - this.entity.settings.adset_start_time) < 86400000) {
        return this.tr('The difference between the start and end date must be at least 24 hours!');
      }

      return null;
    },
  },
  watch: {
    budget(val) {
      const editor = this.$refs.budgetEditor;
      if (val !== null && editor && editor.minBudget !== null) {
        if (val < editor.minBudget) {
          this.debouncedCheckBudget();
        }
        else {
          this.shouldShowBudgetError = false;
        }
      }
    },
    budgetPeriod(val) {
      if (val === 'lifetime') {
        const now = new Date();
        now.setSeconds(0);
        now.setMilliseconds(0);

        this.entity.settings.adset_start_time = now;
        this.entity.settings.adset_end_time = new Date(now.getTime() + 86400000);
      }
      else {
        this.entity.settings.adset_start_time = null;
        this.entity.settings.adset_end_time = null;
        this.entity.settings.adset_lifespan_max_days = null;
      }
    },
  },
  methods: {
    // Intentionally a method and not a computed: it depends on the current time, which is
    // not reactive, so the cached value would become stale on a long-open form.
    isPastDateTime(value) {
      if (!(value instanceof Date) || isNaN(value.getTime())) {
        return false;
      }

      const currentMinute = new Date();
      currentMinute.setSeconds(0);
      currentMinute.setMilliseconds(0);

      return value.getTime() < currentMinute.getTime();
    },
    hasPastLifetimeRange() {
      if (this.budgetPeriod !== 'lifetime' || !this.isLifetimeRangeEditable) {
        return false;
      }

      return this.isPastDateTime(this.entity.settings.adset_start_time)
        || this.isPastDateTime(this.entity.settings.adset_end_time);
    },
    pastLifetimeRangeInfoText() {
      if (this.isPastDateTime(this.entity.settings.adset_start_time)) {
        return this.tr('Start date cannot be in the past!');
      }

      return this.tr('End date cannot be in the past!');
    },
    onBudgetBlur() {
      this.debouncedCheckBudget.cancel();
      const editor = this.$refs.budgetEditor;
      this.shouldShowBudgetError = this.budget && editor && editor.minBudget !== null && this.budget < editor.minBudget;
    },
    validateBudget() {
      const editor = this.$refs.budgetEditor;
      if (editor && editor.isBudgetRequestRunning) {
        this.infoToast(this.tr('Please wait until the process is completed.'));
        return false;
      }

      if (this.invalidLifetimeRange) {
        this.error(this.invalidLifetimeRangeInfoText);
        return false;
      }
      if (this.hasPastLifetimeRange()) {
        this.error(this.pastLifetimeRangeInfoText());
        return false;
      }
      if (!this.budget || (editor && editor.invalidBudget)) {
        this.error(editor ? editor.invalidBudgetInfoText : '');
        return false;
      }

      return true;
    },
  },
};
